import * as CommonAnalytics from './CommonAnalyticsBeacons'
import * as HeroAnalytics from './HeroAnalyticsBeacons'
import { inject } from '../di'
import { ConfigService } from '../ConfigService'
import { FlyerService } from '../FlyerService'
import {
    initSessionStorageSequenceId,
    getSessionStorageSequenceId,
    increaseSequenceId,
} from '../../utils/SessionStorageSequenceId'

// probably this services do not defined in moment of initialization of this component
import { AnalyticsService } from '../AnalyticsService'
import { AvroService, Options } from './AvroService'
import { schemaNames } from './CommonAnalyticsBeacons'

interface HeroAvroAnalyticsChannelInterface {
    FireHeroPostFlyerViewFlyerBeacon(options: Options): void
    FireHeroPostFlyerEngagedVisitFlyerBeacon(options: Options): void
    FireHeroPostFlyerClickFlyerItemBeacon(options: Options): void
    FireHeroPostClosePostBeacon(options: Options): void
    FireHeroPostOpenFlyerBeacon(options: Options): void
    FireHeroPostOpenPostExperienceBeacon(options: Options): void
}

export default class HeroAvroAnalyticsChannel
    implements HeroAvroAnalyticsChannelInterface
{
    defaultEnumType: null = null
    defaultNumber: number = -1

    // budget id will be set after promise is resolved in getBudget() func
    budgetId: number = this.defaultNumber

    @inject flyerService: FlyerService
    @inject config: ConfigService
    @inject avro: AvroService

    // injection of this service does not work in this class with AnalyticsService
    private analyticsService: AnalyticsService = AnalyticsService.getInstance()

    constructor() {
        initSessionStorageSequenceId()
    }

    public FireHeroPostFlyerViewFlyerBeacon(options: Options) {
        this.avro.FireAvroBeacon(
            this.HeroPostFlyerViewFlyerBeacon(
                HeroAnalytics.Schemas.HeroPostFlyerViewFlyer,
                options
            )
        )
    }

    public FireHeroPostFlyerEngagedVisitFlyerBeacon(options: Options) {
        this.avro.FireAvroBeacon(
            this.HeroPostFlyerEngagedVisitFlyerBeacon(
                HeroAnalytics.Schemas.HeroPostFlyerEngagedVisitFlyer,
                options
            )
        )
    }

    public FireHeroPostFlyerClickFlyerItemBeacon(options: Options) {
        this.avro.FireAvroBeacon(
            this.HeroPostFlyerClickFlyerItemBeacon(
                HeroAnalytics.Schemas.HeroPostFlyerClickFlyerItem,
                options
            )
        )
    }

    public FireHeroPostClosePostBeacon(options: Options) {
        this.avro.FireAvroBeacon(
            this.HeroPostClosePostBeacon(
                HeroAnalytics.Schemas.HeroPostClosePost,
                options
            )
        )
    }

    public FireHeroPostOpenFlyerBeacon(options: Options) {
        this.avro.FireAvroBeacon(
            this.HeroPostOpenFlyerBeacon(
                HeroAnalytics.Schemas.HeroPostOpenFlyer,
                options
            )
        )
    }

    public FireHeroPostOpenPostExperienceBeacon(options: Options) {
        this.avro.FireAvroBeacon(
            this.HeroPostOpenPostExperienceBeacon(
                HeroAnalytics.Schemas.HeroPostOpenPostExperience,
                options
            )
        )
    }

    private HeroPostFlyerViewFlyerBeacon(
        schema: HeroAnalytics.Schemas,
        options: Options
    ): HeroAnalytics.HeroBeacon.HeroPostFlyerViewFlyer {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.Hero),
            budget: this.getBudget(options),
            publisher: this.getPublisher(),
            flyer: this.getFlyer(options),
            merchant: this.getMerchant(options),
            heroBase: this.getHeroBase(),
            viewParameters: this.getViewParameters(options),
            schemaInfo: this.getSchemaInfo(schema),
        }
    }

    private HeroPostFlyerEngagedVisitFlyerBeacon(
        schema: HeroAnalytics.Schemas,
        options: Options
    ): HeroAnalytics.HeroBeacon.HeroPostFlyerEngagedVisitFlyer {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.Hero),
            budget: this.getBudget(options),
            publisher: this.getPublisher(),
            flyer: this.getFlyer(options),
            merchant: this.getMerchant(options),
            heroBase: this.getHeroBase(),
            repeat: Boolean(Number(options.repeat)),
            schemaInfo: this.getSchemaInfo(schema),
        }
    }

    private HeroPostFlyerClickFlyerItemBeacon(
        schema: HeroAnalytics.Schemas,
        options: Options
    ): HeroAnalytics.HeroBeacon.HeroPostFlyerClickFlyerItem {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.Hero),
            budget: this.getBudget(options),
            publisher: this.getPublisher(),
            flyer: this.getFlyer(options),
            flyerItem: this.getFlyerItem(options),
            merchant: this.getMerchant(options),
            heroBase: this.getHeroBase(),
            schemaInfo: this.getSchemaInfo(schema),
        }
    }

    private HeroPostClosePostBeacon(
        schema: HeroAnalytics.Schemas,
        options: Options
    ): HeroAnalytics.HeroBeacon.HeroPostClosePost {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.Hero),
            budget: this.getBudget(options),
            publisher: this.getPublisher(),
            flyer: this.getFlyer(options),
            merchant: this.getMerchant(options),
            heroBase: this.getHeroBase(),
            schemaInfo: this.getSchemaInfo(schema),
        }
    }

    private HeroPostOpenFlyerBeacon(
        schema: HeroAnalytics.Schemas,
        options: Options
    ): HeroAnalytics.HeroBeacon.HeroPostOpenFlyer {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.Hero),
            budget: this.getBudget(options),
            publisher: this.getPublisher(),
            flyer: this.getFlyer(options),
            merchant: this.getMerchant(options),
            sourceAction: this.getSourceAction(options),
            heroBase: this.getHeroBase(),
            schemaInfo: this.getSchemaInfo(schema),
        }
    }

    private HeroPostOpenPostExperienceBeacon(
        schema: HeroAnalytics.Schemas,
        options: Options
    ): HeroAnalytics.HeroBeacon.HeroPostOpenPostExperience {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.Hero),
            budget: this.getBudget(options),
            publisher: this.getPublisher(),
            heroBase: this.getHeroBase(),
            schemaInfo: this.getSchemaInfo(schema),
        }
    }

    private getSchemaInfo(
        schema: HeroAnalytics.Schemas
    ): CommonAnalytics.SchemaInfo {
        return {
            schemaId: schema,
            schemaName: { string: schemaNames[schema] },
        }
    }

    private getBudget(options: Options): CommonAnalytics.Budget {
        this.flyerService
            .getFullFlyer(options.flyer_id)
            .then((flyerDetails) => {
                this.budgetId =
                    flyerDetails &&
                    flyerDetails.flyer &&
                    flyerDetails.flyer.budgetId
                        ? flyerDetails.flyer.budgetId
                        : this.defaultNumber
            })
            .catch()

        return {
            budgetId: this.budgetId,
            costModelType: this.defaultEnumType,
        }
    }

    private getMerchant(options: Options): CommonAnalytics.Merchant {
        return {
            merchantId: Number(options.merchant_id),
        }
    }

    private getSourceAction(options: Options): HeroAnalytics.SourceAction {
        switch (options.source_action) {
            case 'cross_browse':
                return HeroAnalytics.SourceAction.CrossBrowse
            case 'direct':
            default:
                return HeroAnalytics.SourceAction.Direct
        }
    }

    private getFlyer(options: Options): CommonAnalytics.Flyer {
        return {
            flyerId: Number(options.flyer_id),
            flyerRunId: Number(options.flyer_run_id),
            flyerTypeId: Number(options.flyer_type_id),
            premium: JSON.parse(options.premium_merchant), // options.premium_merchant can be sting "true" of string "false", JSON.parse() - make it boolean
            storeId: { long: Number(options.merchant_id) },
        }
    }

    private getFlyerItem(options: Options): CommonAnalytics.FlyerItem {
        return {
            id: Number(options.flyer_item_id),
        }
    }
    private getPublisher(): CommonAnalytics.Publisher {
        const publisherId = parseInt(this.config.publisherId)

        return {
            publisherId: publisherId ? publisherId : this.defaultNumber,
            // read readme.md chapter '!important! error in "@flipp/avro-typescript-cli"'
            // @ts-ignore, here should be ts-ignore
            channelId: { long: Number(this.analyticsService.channelId) },
        }
    }

    private getHeroCampaign(): HeroAnalytics.HeroCampaign {
        const moduleId = parseInt(this.config.moduleId)
        const campaignId = parseInt(this.config.campaignId)

        return {
            moduleId: moduleId ? moduleId : this.defaultNumber,
            campaignId: campaignId ? campaignId : this.defaultNumber,
        }
    }

    private getHeroBase(): HeroAnalytics.HeroBase {
        increaseSequenceId()

        return {
            environment: this.avro.getEnvironment(),
            clientParameters: this.avro.getClientParameters(),
            LocationSource: this.avro.getLocationSource(),
            epochMilliseconds: new Date().getTime(),
            sessionId: this.analyticsService.sessionId,
            sequenceId: getSessionStorageSequenceId(),
            appVersion: this.config.BUILD,
            heroCampaign: this.getHeroCampaign(),
        }
    }

    private getViewParameters(options: Options): HeroAnalytics.ViewParameters {
        return {
            left: Number(options.left),
            right: Number(options.right),
            top: Number(options.top),
            bottom: Number(options.bottom),
            resolution: Number(options.resolution),
            userScreenHeight: Number(options.userScreenHeight),
            userScreenWidth: Number(options.userScreenWidth),
            // devicePixelRatio should have type LONG
            // default value is 1, thus we cannot send < 1
            devicePixelRatio:
                window.devicePixelRatio > 1
                    ? Number(window.devicePixelRatio.toFixed())
                    : 1,
        }
    }
}
